import posthog from "posthog-js";

const HEARTBEAT_HEALTHY_MS = 60_000;
const HEARTBEAT_ELEVATED_MS = 30_000;
const CADENCE_STEP_UP_PCT = 70;
const CADENCE_STEP_DOWN_PCT = 65;

export interface MemorySnapshot {
  heapUsedMb: number;
  heapTotalMb: number;
  heapLimitMb: number;
  heapUsedPct: number;
}

type ChromeMemory = {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
};

export function measureCanvasMemoryMb(): number {
  const canvases = document.querySelectorAll("canvas");
  return Array.from(canvases).reduce((sum, c) => sum + (c.width * c.height * 4) / (1024 * 1024), 0);
}

export class MemoryMonitorService {
  private static instance: MemoryMonitorService | null = null;

  private constructor() {}

  private isRunning = false;
  private lastSnapshot: MemorySnapshot | null = null;
  private lastHeartbeatTime = Date.now();
  private sessionStartTime = Date.now();
  private longTaskCount = 0;
  private pdfOpenCount = 0;
  private heartbeatTimerId: ReturnType<typeof setTimeout> | null = null;
  private longTaskObserver: PerformanceObserver | null = null;
  private warningFiredAt70 = false;
  private warningFiredAt85 = false;
  private isElevatedCadence = false;

  static getInstance(): MemoryMonitorService {
    if (!MemoryMonitorService.instance) {
      MemoryMonitorService.instance = new MemoryMonitorService();
    }
    return MemoryMonitorService.instance;
  }

  start(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.sessionStartTime = Date.now();
    this.lastHeartbeatTime = Date.now();
    this.isElevatedCadence = false;
    this.startLongTaskObserver();
    this.scheduleNextHeartbeat(HEARTBEAT_HEALTHY_MS);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  stop(): void {
    this.isRunning = false;
    this.warningFiredAt70 = false;
    this.warningFiredAt85 = false;
    if (this.heartbeatTimerId !== null) {
      clearTimeout(this.heartbeatTimerId);
      this.heartbeatTimerId = null;
    }
    this.longTaskObserver?.disconnect();
    this.longTaskObserver = null;
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
  }

  takeSnapshot(): MemorySnapshot | null {
    const memory = (performance as { memory?: ChromeMemory }).memory;
    if (!memory) return null;
    return {
      heapUsedMb: memory.usedJSHeapSize / (1024 * 1024),
      heapTotalMb: memory.totalJSHeapSize / (1024 * 1024),
      heapLimitMb: memory.jsHeapSizeLimit / (1024 * 1024),
      heapUsedPct: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
    };
  }

  trackPdfOpen(): void {
    this.pdfOpenCount++;
  }

  trackPdfClose(): void {
    this.pdfOpenCount = Math.max(0, this.pdfOpenCount - 1);
  }

  private scheduleNextHeartbeat(delayMs: number): void {
    this.heartbeatTimerId = setTimeout(() => void this.sendHeartbeat(), delayMs);
  }

  private async sendHeartbeat(): Promise<void> {
    try {
      const snapshot = this.takeSnapshot();
      if (!snapshot) {
        this.scheduleNextHeartbeat(
          this.isElevatedCadence ? HEARTBEAT_ELEVATED_MS : HEARTBEAT_HEALTHY_MS
        );
        return;
      }

      const now = Date.now();
      const elapsedMin = (now - this.lastHeartbeatTime) / 60_000;
      const growthRateMbPerMin =
        this.lastSnapshot !== null && elapsedMin > 0
          ? (snapshot.heapUsedMb - this.lastSnapshot.heapUsedMb) / elapsedMin
          : 0;

      const canvases = document.querySelectorAll("canvas");
      const canvasMemoryMb = measureCanvasMemoryMb();

      const heartbeatPayload = {
        heap_used_mb: snapshot.heapUsedMb,
        heap_total_mb: snapshot.heapTotalMb,
        heap_limit_mb: snapshot.heapLimitMb,
        heap_used_pct: snapshot.heapUsedPct,
        heap_growth_rate_mb_per_min: growthRateMbPerMin,
        pdf_count_open: this.pdfOpenCount,
        dom_node_count: document.querySelectorAll("*").length,
        canvas_count: canvases.length,
        canvas_memory_estimated_mb: canvasMemoryMb,
        long_task_count: this.longTaskCount,
        session_duration_s: (now - this.sessionStartTime) / 1000,
      };

      posthog.capture("memory_heartbeat", heartbeatPayload);

      if (snapshot.heapUsedPct >= CADENCE_STEP_UP_PCT) {
        this.isElevatedCadence = true;
      } else if (snapshot.heapUsedPct <= CADENCE_STEP_DOWN_PCT) {
        this.isElevatedCadence = false;
      }

      if (this.isElevatedCadence) {
        void this.attemptMemoryBreakdown();
      }
      this.checkThresholds(snapshot, canvases.length, canvasMemoryMb);

      this.lastSnapshot = snapshot;
      this.lastHeartbeatTime = now;

      this.scheduleNextHeartbeat(
        this.isElevatedCadence ? HEARTBEAT_ELEVATED_MS : HEARTBEAT_HEALTHY_MS
      );
    } catch (err) {
      console.warn("[MemoryMonitorService] Heartbeat failed:", err);
      this.scheduleNextHeartbeat(
        this.isElevatedCadence ? HEARTBEAT_ELEVATED_MS : HEARTBEAT_HEALTHY_MS
      );
    }
  }

  private async attemptMemoryBreakdown(): Promise<void> {
    if (!("measureUserAgentSpecificMemory" in performance)) return;
    try {
      const result = await (
        performance as {
          measureUserAgentSpecificMemory: () => Promise<{
            bytes: number;
            breakdown: Array<{ bytes: number; types: string[] }>;
          }>;
        }
      ).measureUserAgentSpecificMemory();

      const toMb = (bytes: number): number => bytes / (1024 * 1024);
      const BREAKDOWN_BUCKETS = ["JavaScript", "DOM", "Canvas", "CSS"] as const;
      const bucketTotals: Record<(typeof BREAKDOWN_BUCKETS)[number] | "other", number> = {
        JavaScript: 0,
        DOM: 0,
        Canvas: 0,
        CSS: 0,
        other: 0,
      };
      for (const entry of result.breakdown) {
        const bucket = BREAKDOWN_BUCKETS.find((b) => entry.types.includes(b)) ?? "other";
        bucketTotals[bucket] += entry.bytes;
      }

      posthog.capture("memory_breakdown", {
        total_mb: toMb(result.bytes),
        js_mb: toMb(bucketTotals.JavaScript),
        dom_mb: toMb(bucketTotals.DOM),
        canvas_mb: toMb(bucketTotals.Canvas),
        css_mb: toMb(bucketTotals.CSS),
        other_mb: toMb(bucketTotals.other),
      });
    } catch (err) {
      if (!(err instanceof DOMException)) {
        console.warn("[MemoryMonitorService] Unexpected error in attemptMemoryBreakdown:", err);
      }
      // DOMException (SecurityError): cross-origin isolation not configured or non-HTTPS context — expected
    }
  }

  private checkThresholds(
    snapshot: MemorySnapshot,
    canvasCount: number,
    canvasMemoryMb: number
  ): void {
    const base = {
      heap_used_pct: snapshot.heapUsedPct,
      heap_used_mb: snapshot.heapUsedMb,
      heap_limit_mb: snapshot.heapLimitMb,
      pdf_count_open: this.pdfOpenCount,
      canvas_count: canvasCount,
      canvas_memory_estimated_mb: canvasMemoryMb,
    };

    if (snapshot.heapUsedPct >= 85 && !this.warningFiredAt85) {
      this.warningFiredAt85 = true;
      posthog.capture("memory_critical", { ...base, threshold: 85 });
    }
    if (snapshot.heapUsedPct >= 70 && !this.warningFiredAt70) {
      this.warningFiredAt70 = true;
      posthog.capture("memory_warning", { ...base, threshold: 70 });
    }

    if (snapshot.heapUsedPct < 70) {
      this.warningFiredAt70 = false;
      this.warningFiredAt85 = false;
    } else if (snapshot.heapUsedPct < 85) {
      this.warningFiredAt85 = false;
    }
  }

  private startLongTaskObserver(): void {
    if (this.longTaskObserver !== null) return;
    if (!("PerformanceObserver" in window)) return;
    try {
      this.longTaskObserver = new PerformanceObserver((list) => {
        this.longTaskCount += list.getEntries().length;
      });
      this.longTaskObserver.observe({ type: "longtask", buffered: true });
    } catch (err) {
      if (!(err instanceof DOMException)) {
        console.warn("[MemoryMonitorService] Unexpected error starting PerformanceObserver:", err);
      }
      // DOMException: longtask entry type not supported in this browser — expected
    }
  }

  private handleVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") {
      if (this.heartbeatTimerId !== null) {
        clearTimeout(this.heartbeatTimerId);
        this.heartbeatTimerId = null;
      }
    } else if (this.isRunning) {
      this.scheduleNextHeartbeat(HEARTBEAT_HEALTHY_MS);
    }
  };
}
